import type { Ref } from "react";
import { forwardRef, memo, useRef } from "react";
import { mergeRefs } from "react-merge-refs";

import { FormInputBox, InnerInput, LoadingInputIcon } from "../formControl";

import type { IInputProps } from "./types";

import { useReadOnly } from "@/hooks";

const InputV2 = forwardRef(function InputV2(
  props: IInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const {
    containerClassName,
    containerOnClick,
    containerMouseDown,
    onMouseDown,
    validation,
    readOnly: propsReadOnly,
    disabled,
    prefixReact,
    suffixReact,
    bgIcon,
    actionIcon,
    isError,
    isLoading,
    value = "",
    ...rest
  } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <FormInputBox
      validation={validation}
      readOnly={readOnly}
      disabled={disabled}
      className={containerClassName}
      onClick={containerOnClick}
      onMouseDown={(event) => {
        event.preventDefault();
        inputRef.current?.focus();

        containerMouseDown?.(event);
      }}
    >
      {prefixReact}
      <InnerInput
        {...rest}
        value={value}
        ref={mergeRefs([inputRef, ref])}
        readOnly={readOnly}
        disabled={disabled}
        onMouseDown={(event) => {
          // prevent to call `FormInputBox` onMouseDownEvent
          event.stopPropagation();
          onMouseDown?.(event);
        }}
      />
      <LoadingInputIcon isError={isError} isLoading={isLoading}>
        {bgIcon && (
          <span
            className={`d-inline-flex ${readOnly || disabled ? "color-inherit" : "color-primary-main"}`}
          >
            {bgIcon}
          </span>
        )}
        {actionIcon && !disabled && !readOnly && actionIcon}
      </LoadingInputIcon>
      {suffixReact}
    </FormInputBox>
  );
});

export const Input = memo(InputV2);
